import React from 'react';

import {ANY_ARCHIVED_PURPOSE_STRING, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants, {STORABLE_ITEM_TYPES} from 'constants/ModelTypeConstants';
import {
  ChildContainerStatus,
  ContainableSummary,
  LocationCapacityInfo,
  StorableSummary,
  StorableSummaryExtras,
} from 'constants/SampleConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import AppDispatcher from 'lib/Dispatcher';
import {getStorableTypeNameForId, isGridId} from 'samples/SampleUtil';
import {StorablesInputSearchResponse} from 'samples/StorableDAO';
import StorableDetailsDisplay from 'samples/StorableDetailsDisplay';
import {storablesCanBeMovedIntoParent} from 'samples/storables/ParentStorableUtil';
import {
  Filter,
  FilterKey,
  MultiValueFilter,
  Operator,
  SearchContext,
  SingleValueFilter,
  Sort,
  SortKey,
} from 'search/SearchConstants';
import ItemSearchInput, {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import SummaryChip from 'ui-library/chips/SummaryChip';
import {NoResultOption, searchPost} from 'ui-library/inputs/ChipSearchInput';
import ItemOptionContent, {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {OptionData} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import {ElementKeyboardEvent} from 'util/Types';

export function getExtraFromResponse(
  resp: {storableSummaryExtras: StorableSummaryExtras},
  itemId: string
): {
  ancestralStorables: Array<StorableSummary>;
  childContainerStatus: ChildContainerStatus | null;
  locationCapacityInfo: LocationCapacityInfo | null;
  containedItems: Array<ContainableSummary> | null;
} {
  const ancestralStorables: Array<StorableSummary> = resp.storableSummaryExtras.ancestralStorablesById
    ? resp.storableSummaryExtras.ancestralStorablesById[itemId]
    : [];
  assert(ancestralStorables.length >= 1, 'Expected ancestralStorables list to have at least one element');
  assert(ancestralStorables[0].id === itemId, 'Expected first storable to be given item');

  let childContainerStatus: ChildContainerStatus | null = null;
  if (isGridId(itemId) && resp.storableSummaryExtras.childContainerStatusesByGridId) {
    childContainerStatus = resp.storableSummaryExtras.childContainerStatusesByGridId[itemId];
  }

  let locationCapacityInfo: LocationCapacityInfo | null = null;
  if (APIUtil.isLocation(itemId) && resp.storableSummaryExtras.locationCapacityById) {
    locationCapacityInfo = resp.storableSummaryExtras.locationCapacityById[itemId];
  }

  let containedItems: Array<ContainableSummary> | null = null;
  if (
    APIUtil.apiIdToType(itemId) === ModelTypeConstants.CONTAINER &&
    resp.storableSummaryExtras.containedItemsByContainerId
  ) {
    containedItems = resp.storableSummaryExtras.containedItemsByContainerId[itemId] || [];
  }

  return {ancestralStorables, childContainerStatus, locationCapacityInfo, containedItems};
}

function placeholder(storableTypes: Array<string>): string {
  const typeName = storableTypes.length === 1 ? typeToName(storableTypes[0]) : 'inventory';
  return `Search for ${typeName}`;
}

interface StorableSummaryChipProps {
  values: Array<StorableSummary>;
  onRemoveAll: () => void;
}

// Private component for rendering a SummaryChip (or nothing)
function StorableSummaryChip({values, onRemoveAll}: StorableSummaryChipProps): JSX.Element | null {
  return values.length === 0 ? null : (
    <SummaryChip
      size={ChipSize.SMALL}
      itemSummarizer={(value) => value.name}
      itemLabel={getStorableTypeNameForId(values[0].id)}
      items={values}
      onRemove={onRemoveAll}
    />
  );
}

export interface StorableSearchInputProps<TItem> {
  additionalFilters?: Array<Filter>;
  areChipsClickable?: boolean;
  autoFocus?: boolean;
  chipSize?: ChipSize;
  className?: string;
  disabled?: boolean;
  initialQuery?: string;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onChange: (selectedStorables: Array<TItem>) => void;
  // Marked as optional here so this interface can fit to ItemSearchProps but there is a default prop
  // for it so we can rely on it being present in the component.
  storableTypes?: Array<string>;
  selectedItems?: Array<TItem>;
  selectedStorables?: Array<TItem>;
  shouldAllowMultiple?: boolean;
  hideSelectedResults?: boolean;
  query?: string;
  onUpdateQuery?: (query: string) => void;
  // Marked as optional here so this interface can fit to ItemSearchProps but there is a default prop
  // for it so we can rely on it being present in the component.
  summarize?: boolean;
  noResultsOptions?: Array<NoResultOption> | null;
  registryId?: string | null;
  storableSchemaId?: string;
  includeArchived?: boolean;
  placeholder?: string;
  context?: SearchContext;
  isReadOnly?: boolean;
  onSearchStart?: () => void;
  onSearchSuccess?: (resp: StorablesInputSearchResponse) => void;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
}

/**
 * Input to search and select one or more storables.
 *
 * This component takes selected values of shape TItem, but calls the onChange handler with values of
 * shape Storable. This is technically incorrect as the underlying ItemSearchInput doesn't update the
 * selected item values, so onChange may be called with values of shape Item.
 */
export default function StorableSearchInput<TItem extends Item>(
  props: StorableSearchInputProps<TItem>
): JSX.Element {
  const storableTypeFilter: MultiValueFilter | null = props.storableTypes
    ? {key: FilterKey.TYPES, operator: Operator.IS_ONE_OF, value: props.storableTypes}
    : null;
  const storableSchemaIdFilter: MultiValueFilter | null = props.storableSchemaId
    ? {key: FilterKey.SCHEMA, operator: Operator.IS_ONE_OF, value: [props.storableSchemaId]}
    : null;
  const registryIdFilter: SingleValueFilter | null = props.registryId
    ? {key: FilterKey.REGISTRY, operator: Operator.EQ, value: props.registryId}
    : null;
  const archivePurposes = props.includeArchived
    ? [ANY_ARCHIVED_PURPOSE_STRING, NOT_ARCHIVED_PURPOSE_STRING]
    : [NOT_ARCHIVED_PURPOSE_STRING];
  const archivePurposeFilter: MultiValueFilter = {
    key: FilterKey.ARCHIVE_PURPOSES,
    operator: Operator.IS_ONE_OF,
    value: archivePurposes,
  };
  const filters: Array<Filter> = [
    storableTypeFilter,
    storableSchemaIdFilter,
    registryIdFilter,
    archivePurposeFilter,
  ].filter<SingleValueFilter | MultiValueFilter>(
    (filter: Filter | null): filter is SingleValueFilter | MultiValueFilter => filter != null
  );
  const sorts: Array<Sort> = props.includeArchived
    ? [
        {key: SortKey.SORT_ARCHIVED_PURPOSE, reverse: false},
        {key: SortKey.SORT_RELEVANCE_KEY, reverse: false},
      ]
    : [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}];

  function handleSearchSuccess(searchResponse: StorablesInputSearchResponse): void {
    props.onSearchSuccess?.(searchResponse);
    AppDispatcher.dispatch({
      actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
      storables: Object.values(searchResponse.storables),
      storableSummaryExtras: searchResponse.storableSummaryExtras,
    });
  }

  function getSelectedItems(): Array<TItem> {
    return props.selectedItems || props.selectedStorables!;
  }

  function getOptionDatasFromResponse(resp: {
    storables: Array<StorableSummary>;
    storableSummaryExtras: StorableSummaryExtras;
  }): Array<OptionData> {
    const idsToIgnore = props.hideSelectedResults ? getSelectedItems().map((item) => item.id) : [];
    const filteredItems = resp.storables.filter((item) => !idsToIgnore.includes(item.id));
    return filteredItems
      .map((item) => {
        const {
          isValid: storablesCanBeMoved,
          errorMessage,
          hasDisallowedOrInvalidSchemaChildren,
        } = storablesCanBeMovedIntoParent({
          destinationStorable: item,
          storablesToBeMoved: props.storablesToBeMoved,
          extras: resp.storableSummaryExtras,
        });
        return {...item, storablesCanBeMoved, errorMessage, hasDisallowedOrInvalidSchemaChildren};
      })
      .sort((a, b) => Number(b.storablesCanBeMoved) - Number(a.storablesCanBeMoved))
      .map((item) => {
        const value = {
          extra: {
            ...getExtraFromResponse(resp, item.id),
            hasDisallowedOrInvalidSchemaChildren: item.hasDisallowedOrInvalidSchemaChildren,
          },
          item,
          item_id: item.id,
          name: item.name,
          type: APIUtil.apiIdToType(item.id),
        };
        return {
          name: item.name,
          icon: getIconIdForSearchResult(value),
          // See also `renderOptionContent` below, which currently expects `disabled` to mean the storables
          // to be moved cannot fit. If we disable options for other reasons, that function will need change.
          disabled: !item.storablesCanBeMoved,
          value,
          disabledMessage: item.errorMessage ?? undefined,
        };
      });
  }

  function renderOptionContent(optionData: OptionData): JSX.Element {
    // `optionData.disabled` was calculated in `getOptionDatasFromResponse` and (for now) always means the
    // grid doesn't have space for the storables to be moved.
    return (
      <ItemOptionContent
        optionData={optionData}
        tooltip={
          optionData.disabled && optionData.disabledMessage != null
            ? {text: optionData.disabledMessage}
            : null
        }
        searchResultChildren={
          <StorableDetailsDisplay
            searchResult={optionData.value}
            shouldCallOutCapacity={
              optionData.disabled && !optionData.value?.extra?.hasDisallowedOrInvalidSchemaChildren
            }
            includeLocationCapacityInfo={
              (props.storablesToBeMoved?.length ?? 0) > 0 &&
              !optionData.value?.extra?.hasDisallowedOrInvalidSchemaChildren
            }
          />
        }
      />
    );
  }

  return (
    <ItemSearchInput<TItem, StorablesInputSearchResponse>
      areChipsClickable={props.areChipsClickable}
      autoFocus={props.autoFocus}
      chipSize={props.chipSize}
      className={props.className}
      disabled={props.disabled}
      isReadOnly={props.isReadOnly}
      doSearch={searchPost}
      initialQuery={props.initialQuery || ''}
      getExtraFromResponse={getExtraFromResponse}
      getOptionDatasFromResponse={getOptionDatasFromResponse}
      itemKey="storables"
      onKeyDown={props.onKeyDown}
      onChange={props.onChange}
      fetchParams={{
        filters: [...filters, ...(props.additionalFilters ?? [])],
        offset: 0,
        sorts,
        context: props.context,
      }}
      placeholder={props.placeholder ?? placeholder(props.storableTypes!)}
      searchURL="/1/api/storables:input-search"
      selectedItems={getSelectedItems()}
      shouldAllowMultiple={props.shouldAllowMultiple}
      hideSelectedResults={props.hideSelectedResults}
      query={props.query}
      onUpdateQuery={props.onUpdateQuery}
      renderChips={
        props.summarize
          ? (values) => (
              <StorableSummaryChip
                values={values}
                onRemoveAll={() => {
                  props.onChange([]);
                }}
              />
            )
          : null
      }
      summarize={props.summarize}
      noResultsOptions={props.noResultsOptions}
      onSearchStart={props.onSearchStart}
      onSearchSuccess={handleSearchSuccess}
      renderOptionContent={renderOptionContent}
      chipSearchInputKey={props.storablesToBeMoved?.length}
    />
  );
}

StorableSearchInput.defaultProps = {
  storableTypes: [...STORABLE_ITEM_TYPES],
  summarize: false,
};

type StorableSearchInputWithItemsProps = {
  selectedItems: Array<Item>;
  onChange: (newItems: Array<Item>) => void;
} & Omit<StorableSearchInputProps<Item>, 'onChange' | 'selectedItems' | 'selectedStorables'>;

/**
 * Input to search and select one or more storables.
 *
 * Use this component when only the ids of the storables are available and needed. It expects the selected
 * values to be of shape Item, and calls the onChange handler with values of shape Item.
 */
export function StorableSearchInputWithItems(props: StorableSearchInputWithItemsProps): JSX.Element {
  return <StorableSearchInput<Item> {...props} selectedItems={props.selectedItems} />;
}
